import { useState } from 'react';
import {
  User,
  Users,
  Hammer,
  ClipboardCheck,
  PenTool,
  FileSignature,
  BookOpen,
  Building2,
  MapPin,
  Phone,
  Globe,
  RefreshCcw,
  Pencil,
  Plus,
  ChevronDown,
  ChevronUp,
  Sparkles,
} from 'lucide-react';
import { Input } from '../../../components/ui/Input';
import { Textarea } from '../../../components/ui/Textarea';
import { Select } from '../../../components/ui/Select';
import { FormLabel } from '../../../components/ui/FormLabel';
import {
  createEmptyCompanyInfo,
  mergeOptionsWithLegacy,
  normalizeRiskAssessmentStatus,
  resolveCompanyDisplay,
} from './eicDeclarationUtils';

const DECLARATION_SECTIONS = [
  {
    key: 'design',
    label: 'DESIGN',
    Icon: PenTool,
    showExceptions: true,
    showRiskAssessment: true,
    allowSecondPair: true,
    engineerLabel: 'Design Engineer',
    companyLabel: 'Design Company',
  },
  {
    key: 'construction',
    label: 'CONSTRUCTION',
    Icon: Hammer,
    showExceptions: false,
    showRiskAssessment: false,
    allowSecondPair: false,
    engineerLabel: 'Construction Engineer',
    companyLabel: 'Construction Company',
  },
  {
    key: 'inspection',
    label: 'INSPECTION & TESTING',
    Icon: ClipboardCheck,
    showExceptions: false,
    showRiskAssessment: false,
    allowSecondPair: false,
    engineerLabel: 'Inspection & Testing Engineer',
    companyLabel: 'Inspection & Testing Company',
  },
];

const DateFieldWithToday = ({ id, label, value, onChange }) => (
  <div>
    <FormLabel htmlFor={id}>{label}</FormLabel>
    <div className="flex items-center gap-2 mt-1">
      <Input
        id={id}
        type="date"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        variant="borderless"
        className="flex-1 h-9"
      />
      <button
        type="button"
        onClick={() => onChange(new Date().toISOString().slice(0, 10))}
        className="inline-flex items-center h-9 px-3 text-xs border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600"
      >
        Today
      </button>
    </div>
  </div>
);

const RiskAssessmentToggle = ({ value, onChange }) => {
  const status = normalizeRiskAssessmentStatus(value);
  return (
    <div>
      <FormLabel>Risk assessment attached</FormLabel>
      <div className="inline-flex mt-1 rounded-md shadow-sm divide-x divide-gray-200 dark:divide-gray-700">
        {['yes', 'na'].map((option, idx, arr) => {
          const isSelected = status === option;
          const rounding = idx === 0 ? 'rounded-l-md' : idx === arr.length - 1 ? 'rounded-r-md' : '';
          return (
            <button
              key={option}
              type="button"
              onClick={() => onChange(option)}
              className={`px-4 py-2 text-xs font-medium border border-gray-300 dark:border-gray-600 min-w-[56px] ${rounding} ${
                isSelected
                  ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400'
                  : 'bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-600'
              }`}
            >
              {option === 'yes' ? 'YES' : 'N/A'}
            </button>
          );
        })}
      </div>
    </div>
  );
};

const PresetTextarea = ({ id, label, value, onChange, onOpenPreset, regulationNote, rows = 5 }) => (
  <div>
    <div className="flex items-center justify-between mb-2">
      <FormLabel htmlFor={id}>{label}</FormLabel>
      <button
        type="button"
        onClick={onOpenPreset}
        className="inline-flex items-center gap-1 text-xs font-medium text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300"
      >
        <Sparkles className="h-3.5 w-3.5" />
        Presets
      </button>
    </div>
    <Textarea
      id={id}
      rows={rows}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={`Enter ${label.toLowerCase()}...`}
    />
    {regulationNote && (
      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{regulationNote}</p>
    )}
  </div>
);

const CompanyInfoCard = ({
  company,
  tenantCompanyInfo,
  onRefreshFromTenant,
  onToggleCustom,
  onCustomChange,
}) => {
  const display = resolveCompanyDisplay(company, tenantCompanyInfo);
  const isCustom = !!company?.useCustom;

  return (
    <div className="space-y-3">
      {isCustom ? (
        <div className="space-y-3 rounded-lg border border-gray-200 dark:border-gray-600 p-3 bg-gray-50 dark:bg-gray-900/40">
          <div>
            <FormLabel>Company name</FormLabel>
            <Input
              value={company.companyName || ''}
              onChange={(e) => onCustomChange({ companyName: e.target.value })}
              className="w-full h-9 mt-1"
            />
          </div>
          <div>
            <FormLabel>Enrolment No</FormLabel>
            <Input
              value={company.enrolmentNo || ''}
              onChange={(e) => onCustomChange({ enrolmentNo: e.target.value })}
              className="w-full h-9 mt-1"
            />
          </div>
          <div>
            <FormLabel>Address</FormLabel>
            <Textarea
              rows={2}
              value={company.address || ''}
              onChange={(e) => onCustomChange({ address: e.target.value })}
              className="mt-1"
            />
          </div>
          <div>
            <FormLabel>Phone</FormLabel>
            <Input
              value={company.phone || ''}
              onChange={(e) => onCustomChange({ phone: e.target.value })}
              className="w-full h-9 mt-1"
            />
          </div>
          <div>
            <FormLabel>Website</FormLabel>
            <Input
              value={company.website || ''}
              onChange={(e) => onCustomChange({ website: e.target.value })}
              className="w-full h-9 mt-1"
            />
          </div>
        </div>
      ) : (
        <div className="rounded-lg border border-gray-200 dark:border-gray-600 p-3 bg-gray-50 dark:bg-gray-900/40 space-y-2">
          <div className="flex items-center gap-2">
            <Building2 className="h-4 w-4 text-gray-500 dark:text-gray-400 flex-shrink-0" />
            <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">
              {display.companyName || 'Company name not set'}
            </span>
          </div>
          {display.enrolmentNo && (
            <p className="text-xs text-gray-600 dark:text-gray-400">Enrolment No: {display.enrolmentNo}</p>
          )}
          {display.address && (
            <p className="flex items-start gap-2 text-xs text-gray-600 dark:text-gray-400">
              <MapPin className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
              <span>{display.address}</span>
            </p>
          )}
          {display.phone && (
            <p className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-400">
              <Phone className="h-3.5 w-3.5 flex-shrink-0" />
              <span>{display.phone}</span>
            </p>
          )}
          {display.website && (
            <p className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-400">
              <Globe className="h-3.5 w-3.5 flex-shrink-0" />
              <span>{display.website}</span>
            </p>
          )}
        </div>
      )}
      <div className="flex flex-col gap-1">
        <button
          type="button"
          onClick={onRefreshFromTenant}
          className="inline-flex items-center gap-1 text-xs font-medium text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 w-fit"
        >
          <RefreshCcw className="h-3.5 w-3.5" />
          Update with latest company info
        </button>
        <button
          type="button"
          onClick={onToggleCustom}
          className="inline-flex items-center gap-1 text-xs font-medium text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 w-fit"
        >
          <Pencil className="h-3.5 w-3.5" />
          {isCustom ? 'Use company settings…' : 'Enter custom values…'}
        </button>
      </div>
    </div>
  );
};

const DeclarationPairCard = ({
  title,
  engineer,
  engineerDate,
  company,
  tenantCompanyInfo,
  engineerOptions,
  signatoriesLoading,
  onEngineerChange,
  onEngineerDateChange,
  onCompanyChange,
  onRefreshCompany,
}) => {
  const [expanded, setExpanded] = useState(true);

  return (
    <div className="rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden bg-[#0B0D10]">
      <button
        type="button"
        onClick={() => setExpanded((prev) => !prev)}
        className="w-full flex items-center justify-between px-3 py-2.5 bg-[#0B0D10] text-white text-sm font-medium"
      >
        <span>{title}</span>
        {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
      </button>
      {expanded && (
        <div className="p-4 space-y-4 bg-[#0B0D10]">
          {title.toLowerCase().includes('company') ? (
            <CompanyInfoCard
              company={company}
              tenantCompanyInfo={tenantCompanyInfo}
              onRefreshFromTenant={() => onRefreshCompany()}
              onToggleCustom={() => onCompanyChange({ useCustom: !company?.useCustom })}
              onCustomChange={(patch) => onCompanyChange(patch)}
            />
          ) : (
            <>
              <div>
                <FormLabel>Engineer</FormLabel>
                <Select
                  value={engineer?.value || ''}
                  onChange={(e) => onEngineerChange(e.target.value)}
                  className="w-full h-9 mt-1"
                >
                  <option value="">{signatoriesLoading ? 'Loading team…' : 'Select team member…'}</option>
                  {engineerOptions.map((opt) => (
                    <option key={opt.value} value={opt.value} disabled={opt.disabled}>
                      {opt.label}
                    </option>
                  ))}
                </Select>
                <button
                  type="button"
                  className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300"
                >
                  <Pencil className="h-3.5 w-3.5" />
                  Enter custom values…
                </button>
              </div>
              <DateFieldWithToday
                id={`${title}-date`}
                label="Date"
                value={engineerDate}
                onChange={onEngineerDateChange}
              />
            </>
          )}
        </div>
      )}
    </div>
  );
};

const SectionHeader = ({ label, Icon }) => (
  <div className="flex items-center gap-2 px-4 py-3 bg-orange-500 text-white font-semibold text-sm tracking-wide">
    <Icon className="h-4 w-4 flex-shrink-0" />
    <span>{label}</span>
  </div>
);

const DeclarationModeToggle = ({ mode, onChange }) => (
  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6">
    {[
      {
        value: 'single',
        label: 'Single declaration',
        description: 'One contractor for all design, construction and inspection.',
        Icon: User,
      },
      {
        value: 'multiple',
        label: 'Multiple declarations',
        description: 'Multiple contractors for design, construction and inspection.',
        Icon: Users,
      },
    ].map((option) => {
      const isActive = mode === option.value;
      const Icon = option.Icon;
      return (
        <button
          key={option.value}
          type="button"
          onClick={() => onChange(option.value)}
          className={`text-left rounded-xl border p-4 transition-colors ${
            isActive
              ? 'bg-white dark:bg-gray-100 border-gray-200 text-gray-900'
              : 'bg-gray-800 dark:bg-gray-900 border-gray-700 text-gray-300 hover:border-gray-600'
          }`}
        >
          <div className="flex items-start gap-3">
            <Icon className={`h-5 w-5 mt-0.5 flex-shrink-0 ${isActive ? 'text-gray-900' : 'text-gray-400'}`} />
            <div>
              <p className={`text-sm font-semibold ${isActive ? 'text-gray-900' : 'text-gray-100'}`}>
                {option.label}
              </p>
              <p className={`text-xs mt-1 ${isActive ? 'text-gray-600' : 'text-gray-400'}`}>
                {option.description}
              </p>
            </div>
          </div>
        </button>
      );
    })}
  </div>
);

const EicDeclarationTab = ({
  declarationMode,
  onDeclarationModeChange,
  bs7671Departures,
  onBs7671DeparturesChange,
  permittedExceptions,
  onPermittedExceptionsChange,
  riskAssessmentAttached,
  onRiskAssessmentAttachedChange,
  inspectedBy,
  onInspectedByChange,
  inspectedDate,
  onInspectedDateChange,
  authorisedBy,
  onAuthorisedByChange,
  authorisedDate,
  onAuthorisedDateChange,
  singleCompany,
  onSingleCompanyChange,
  sectionDeclarations,
  onSectionDeclarationsChange,
  tenantCompanyInfo,
  onRefreshTenantCompanyInfo,
  signatoryOptions,
  signatoriesLoading,
  handleSignatorySelectChange,
  inspectedByOptions,
  authorisedByOptions,
  onOpenPresetDrawer,
  onOpenPresetForTarget,
}) => {
  const updateSection = (sectionKey, patch) => {
    onSectionDeclarationsChange({
      ...sectionDeclarations,
      [sectionKey]: {
        ...sectionDeclarations[sectionKey],
        ...patch,
      },
    });
  };

  const updateSectionCompany = (sectionKey, patch, pairKey = null) => {
    const section = sectionDeclarations[sectionKey];
    if (pairKey === 'second') {
      updateSection(sectionKey, {
        secondPair: {
          ...section.secondPair,
          company: { ...section.secondPair.company, ...patch },
        },
      });
      return;
    }
    updateSection(sectionKey, {
      company: { ...section.company, ...patch },
    });
  };

  const refreshSectionCompany = (sectionKey, pairKey = null) => {
    const fresh = { ...tenantCompanyInfo, useCustom: false };
    if (pairKey === 'second') {
      updateSection(sectionKey, {
        secondPair: {
          ...sectionDeclarations[sectionKey].secondPair,
          company: fresh,
        },
      });
      return;
    }
    updateSection(sectionKey, { company: fresh });
  };

  const renderDeclarationPair = (sectionKey, section, pairKey = null) => {
    const config = DECLARATION_SECTIONS.find((s) => s.key === sectionKey);
    const pair = pairKey === 'second' ? section.secondPair : section;
    if (!pair) return null;

    const engineer = pair.engineer;
    const engineerDate = pair.engineerDate;
    const company = pair.company;
    const engineerOptions = mergeOptionsWithLegacy(signatoryOptions, engineer);

    const handleEngineerChange = (value) => {
      handleSignatorySelectChange(value, (next) => {
        if (pairKey === 'second') {
          updateSection(sectionKey, { secondPair: { ...section.secondPair, engineer: next } });
        } else {
          updateSection(sectionKey, { engineer: next });
        }
      }, engineerOptions);
    };

    const handleEngineerDateChange = (date) => {
      if (pairKey === 'second') {
        updateSection(sectionKey, { secondPair: { ...section.secondPair, engineerDate: date } });
      } else {
        updateSection(sectionKey, { engineerDate: date });
      }
    };

    return (
      <div
        key={pairKey || 'primary'}
        className={`grid grid-cols-1 md:grid-cols-2 gap-4${pairKey === 'second' ? ' mt-6' : ''}`}
      >
        <DeclarationPairCard
          title={config.engineerLabel}
          engineer={engineer}
          engineerDate={engineerDate}
          engineerOptions={engineerOptions}
          signatoriesLoading={signatoriesLoading}
          onEngineerChange={handleEngineerChange}
          onEngineerDateChange={handleEngineerDateChange}
        />
        <DeclarationPairCard
          title={config.companyLabel}
          company={company}
          tenantCompanyInfo={tenantCompanyInfo}
          onCompanyChange={(patch) => updateSectionCompany(sectionKey, patch, pairKey)}
          onRefreshCompany={() => refreshSectionCompany(sectionKey, pairKey)}
        />
      </div>
    );
  };

  return (
    <section>
      <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">Declaration</h2>

      <DeclarationModeToggle mode={declarationMode} onChange={onDeclarationModeChange} />

      {declarationMode === 'single' ? (
        <div className="rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden bg-white dark:bg-gray-800 mb-6">
          <SectionHeader label="FOR THE DESIGN, CONSTRUCTION, INSPECTION AND TESTING" Icon={BookOpen} />
          <div className="p-4 bg-[#0B0D10]">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="space-y-6">
                <PresetTextarea
                  id="single-bs7671-departures"
                  label="Details of departures from BS 7671, as amended"
                  value={bs7671Departures}
                  onChange={onBs7671DeparturesChange}
                  onOpenPreset={() => onOpenPresetDrawer('bs7671Departures')}
                  regulationNote="Regulations 120.3, 133.1.2, 133.1.3 and 133.5"
                />
                <PresetTextarea
                  id="single-permitted-exceptions"
                  label="Details of permitted exceptions"
                  value={permittedExceptions}
                  onChange={onPermittedExceptionsChange}
                  onOpenPreset={() => onOpenPresetDrawer('permittedExceptions')}
                  regulationNote="Regulations 411.3.3"
                />
                <RiskAssessmentToggle
                  value={riskAssessmentAttached}
                  onChange={onRiskAssessmentAttachedChange}
                />
              </div>
              <div className="space-y-4">
                <div>
                  <FormLabel htmlFor="single-inspected-by">
                    For the design, construction, inspection and testing
                  </FormLabel>
                  <Select
                    id="single-inspected-by"
                    value={inspectedBy.value}
                    onChange={(e) => handleSignatorySelectChange(e.target.value, onInspectedByChange, inspectedByOptions)}
                    className="w-full h-9 mt-1"
                  >
                    <option value="">{signatoriesLoading ? 'Loading team…' : 'Select team member…'}</option>
                    {inspectedByOptions.map((opt) => (
                      <option key={opt.value} value={opt.value} disabled={opt.disabled}>
                        {opt.label}
                      </option>
                    ))}
                  </Select>
                </div>
                <DateFieldWithToday
                  id="single-inspected-date"
                  label="Date"
                  value={inspectedDate}
                  onChange={onInspectedDateChange}
                />
                <div>
                  <FormLabel htmlFor="single-authorised-by">Certificate authorised for issue by</FormLabel>
                  <Select
                    id="single-authorised-by"
                    value={authorisedBy.value}
                    onChange={(e) => handleSignatorySelectChange(e.target.value, onAuthorisedByChange, authorisedByOptions)}
                    className="w-full h-9 mt-1"
                  >
                    <option value="">{signatoriesLoading ? 'Loading team…' : 'Select team member…'}</option>
                    {authorisedByOptions.map((opt) => (
                      <option key={opt.value} value={opt.value} disabled={opt.disabled}>
                        {opt.label}
                      </option>
                    ))}
                  </Select>
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400 italic">
                    Only QS and Admin roles can sign off the certificate.
                  </p>
                </div>
                <DateFieldWithToday
                  id="single-authorised-date"
                  label="Date"
                  value={authorisedDate}
                  onChange={onAuthorisedDateChange}
                />
                <div>
                  <FormLabel>Trading title</FormLabel>
                  <div className="mt-2">
                    <CompanyInfoCard
                      company={singleCompany}
                      tenantCompanyInfo={tenantCompanyInfo}
                      onRefreshFromTenant={onRefreshTenantCompanyInfo}
                      onToggleCustom={() => onSingleCompanyChange({ useCustom: !singleCompany?.useCustom })}
                      onCustomChange={(patch) => onSingleCompanyChange(patch)}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <>
          {DECLARATION_SECTIONS.map((sectionConfig) => {
            const section = sectionDeclarations[sectionConfig.key] || {};
            return (
              <div
                key={sectionConfig.key}
                className="rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden bg-white dark:bg-gray-800 mb-6"
              >
                <SectionHeader label={sectionConfig.label} Icon={sectionConfig.Icon} />
                <div className="p-4 space-y-6 bg-[#0B0D10]">
                  {sectionConfig.showExceptions ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <PresetTextarea
                        id={`${sectionConfig.key}-departures`}
                        label="Details of departures from BS 7671, as amended"
                        value={section.bs7671Departures || ''}
                        onChange={(val) => updateSection(sectionConfig.key, { bs7671Departures: val })}
                        onOpenPreset={() => onOpenPresetForTarget('bs7671Departures', (val) => updateSection(sectionConfig.key, { bs7671Departures: val }))}
                        regulationNote="Regulations 120.3, 133.1.2, 133.1.3 and 133.5"
                      />
                      <div className="space-y-4">
                        <PresetTextarea
                          id={`${sectionConfig.key}-exceptions`}
                          label="Details of permitted exceptions"
                          value={section.permittedExceptions || ''}
                          onChange={(val) => updateSection(sectionConfig.key, { permittedExceptions: val })}
                          onOpenPreset={() => onOpenPresetForTarget('permittedExceptions', (val) => updateSection(sectionConfig.key, { permittedExceptions: val }))}
                          regulationNote="Regulations 411.3.3"
                        />
                        <RiskAssessmentToggle
                          value={section.riskAssessmentStatus}
                          onChange={(val) => updateSection(sectionConfig.key, { riskAssessmentStatus: val })}
                        />
                      </div>
                    </div>
                  ) : (
                    <PresetTextarea
                      id={`${sectionConfig.key}-departures`}
                      label="Details of departures from BS 7671, as amended"
                      value={section.bs7671Departures || ''}
                      onChange={(val) => updateSection(sectionConfig.key, { bs7671Departures: val })}
                      onOpenPreset={() => onOpenPresetForTarget('bs7671Departures', (val) => updateSection(sectionConfig.key, { bs7671Departures: val }))}
                      regulationNote="Regulations 120.3, 133.1.2, 133.1.3 and 133.5"
                    />
                  )}

                  <div className="border-t border-dashed border-gray-300 dark:border-gray-600 pt-4">
                    <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-4">Declaration:</h4>
                    {renderDeclarationPair(sectionConfig.key, section)}
                    {section.secondPair && renderDeclarationPair(sectionConfig.key, section, 'second')}
                    {sectionConfig.allowSecondPair && !section.secondPair && (
                      <button
                        type="button"
                        onClick={() => updateSection(sectionConfig.key, {
                          secondPair: {
                            engineer: { id: '', label: '', type: '', value: '' },
                            engineerDate: '',
                            company: createEmptyCompanyInfo(),
                          },
                        })}
                        className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300"
                      >
                        <Plus className="h-4 w-4" />
                        Add 2nd Design Engineer/Company
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}

          <div className="rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden bg-white dark:bg-gray-800 mb-6">
            <SectionHeader label="AUTHORISED BY" Icon={FileSignature} />
            <div className="p-4 bg-[#0B0D10]">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <FormLabel htmlFor="multi-authorised-by">Certificate authorised for issue by</FormLabel>
                  <Select
                    id="multi-authorised-by"
                    value={authorisedBy.value}
                    onChange={(e) => handleSignatorySelectChange(e.target.value, onAuthorisedByChange, authorisedByOptions)}
                    className="w-full h-9 mt-1"
                  >
                    <option value="">{signatoriesLoading ? 'Loading team…' : 'Select team member…'}</option>
                    {authorisedByOptions.map((opt) => (
                      <option key={opt.value} value={opt.value} disabled={opt.disabled}>
                        {opt.label}
                      </option>
                    ))}
                  </Select>
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400 italic">
                    Only QS and Admin roles can sign off the certificate.
                  </p>
                </div>
                <DateFieldWithToday
                  id="multi-authorised-date"
                  label="Date"
                  value={authorisedDate}
                  onChange={onAuthorisedDateChange}
                />
              </div>
            </div>
          </div>
        </>
      )}
    </section>
  );
};

export default EicDeclarationTab;
